import { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import classnames from 'classnames'

import { ComponentTheme } from '~/components/themes'
import { useSettingsContext } from '~/contexts/settingsContext'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'

import Button, { ButtonTypes, ButtonVariants } from '../Button/Button'
import CloseButton from '../CloseButton/CloseButton'
import type { IconTypes } from '../Icon/Icon'
import { Icon } from '../Icon/Icon'
import Socials from '../Socials/Socials'
import Text, { TextSizes } from '../Text/Text'

import styles from './SocialShare.module.css'

export interface SocialShareProps {
  articleTitle: string
  testID?: string
  urlToShare?: string
  showSocials?: boolean
  dialogTop?: boolean
  theme?: ComponentTheme
  shareButtonTheme?: 'magazine' | 'default'
}

export default function SocialShare({
  articleTitle,
  showSocials = true,
  dialogTop = false,
  testID,
  urlToShare,
  theme,
  shareButtonTheme = 'default',
}: SocialShareProps) {
  const tCommon = useTranslations('common')
  const router = useRouter()
  const baseUrl =
    process.env.NEXT_PUBLIC_SITE_URL || 'https://www.myartbroker.com'
  // server side seems to render /en/edition
  // @todo - this will need to take into consideration the locale once internationalisation is done
  const shareUrl =
    urlToShare || `${baseUrl}${router.asPath.replace('/en/edition', '')}`
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [copied, setCopied] = useState<boolean>(false)
  const { instagram, twitter, tiktok, youtube } = useSettingsContext()

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCopied(false)
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [copied])

  const closeDialog = () => {
    setDialogOpen(false)
  }

  const dialogContent = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const close = (event: any) => {
      // TODO:: what is this contains and negative condition checks?
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      !dialogContent.current?.contains(event.target) && closeDialog()
    }

    window.addEventListener('mousedown', close)
    window.addEventListener('touchstart', close, { passive: true })

    return () => {
      window.removeEventListener('mousedown', close)
      window.removeEventListener('touchstart', close)
    }
  }, [])

  const shareData = [
    {
      url: shareUrl,
      icon: 'ShareFacebook',
      title: 'Facebook',
    },
    {
      url: shareUrl,
      icon: 'ShareEmail',
      title: 'Email',
    },
    {
      url: shareUrl,
      icon: 'ShareLinkedin',
      title: 'LinkedIn',
    },
    {
      url: shareUrl,
      icon: 'ShareWhatsapp',
      title: 'WhatsApp',
    },
    {
      url: shareUrl,
      icon: 'ShareTwitter',
      title: 'Twitter',
    },
  ]

  const urls = [
    {
      id: 'ShareTwitter',
      url: 'https://twitter.com/intent/tweet?text=',
      utm: encodeURIComponent(
        '?utm_source=organic+social&utm_medium=twitter&utm_campaign=twitter'
      ),
    },
    {
      id: 'ShareFacebook',
      url: 'https://www.facebook.com/sharer/sharer.php?u=',
      utm: encodeURIComponent(
        '?utm_source=organic+social&utm_medium=facebook&utm_campaign=facebook'
      ),
    },
    {
      id: 'ShareLinkedin',
      url: 'https://www.linkedin.com/shareArticle?mini=true&url=',
      utm: encodeURIComponent(
        '?utm_source=organic+social&utm_medium=linkedin&utm_campaign=linkedin'
      ),
    },
    {
      id: 'ShareEmail',
      url: `mailto:?subject=Sharing: ${articleTitle}&body=`,
      utm: encodeURIComponent(
        '?utm_source=email&utm_medium=email&utm_campaign=articles'
      ),
    },
    {
      id: 'ShareWhatsapp',
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(
        articleTitle
      )}%20`,
      utm: encodeURIComponent(
        '?utm_source=organic+social&utm_medium=whatsapp&utm_campaign=whatsapp'
      ),
    },
  ]

  const copyText = async () => {
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
  }

  const handleClick = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>,
    link: string,
    icon: string
  ) => {
    e.preventDefault()
    const currentShareButton = urls.filter(item => item.id === icon)[0]
    const shareLink = `${currentShareButton.url}${link}${currentShareButton.utm}`

    const shareWindow = window.open(
      shareLink,
      'Share',
      'left=0,top=0,width=320,height=600'
    )

    if (shareLink.includes('mailto')) {
      setTimeout(() => {
        shareWindow?.close()
      }, 500)
    }
  }

  return (
    <div
      className={classnames(
        styles.base,
        shareButtonTheme === 'magazine' && styles.magazine
      )}
      data-testid={testID}
    >
      <button
        className={classnames(
          styles.openDialog,
          theme === ComponentTheme.black && styles.whiteBtn,
          shareButtonTheme === 'magazine' && styles.openDialogMagazine
        )}
        onClick={() => setDialogOpen(true)}
      >
        <Icon type="Share" width={16} height={16} />
        <Text
          size={
            shareButtonTheme !== 'magazine'
              ? TextSizes.primaryTitleXSml
              : TextSizes.bodySml
          }
        >
          {tCommon('share')}
        </Text>
      </button>
      <div
        className={classnames(
          styles.dialog,
          dialogOpen && styles.dialogOpen,
          dialogTop && styles.dialogTop
        )}
        ref={dialogContent}
      >
        <div className={styles.dialogHeader}>
          <Text size={TextSizes.primaryTitleMed}>
            {tCommon('shareArticle')}
          </Text>
          <CloseButton onClick={closeDialog} variant="small" />
        </div>

        <div className={styles.copyField}>
          <input type="text" value={shareUrl} readOnly={true} />
          <Button
            reducedPadding={true}
            className={styles.copyButton}
            button={{ type: ButtonTypes.button, onClick: copyText }}
          >
            {copied ? tCommon('copied') : tCommon('copy')}
          </Button>
        </div>

        <ul className={styles.socials}>
          {shareData.map(socialLink => {
            return (
              <li key={socialLink.icon} className={styles.item}>
                <Button
                  button={{
                    type: ButtonTypes.button,
                    onClick: e => {
                      handleClick(e, socialLink.url, socialLink.icon)
                      analyticsPusher(analyticsEventTypes.gaEvent, {
                        gaEventName: `${socialLink.title} share clicked`,
                      })
                    },
                  }}
                  variant={ButtonVariants.textLink}
                  testID={testID && `button-${testID}`}
                  className={styles.button}
                >
                  <span className={styles.socialLink}>
                    <Icon
                      type={socialLink.icon as IconTypes}
                      width={24}
                      height={24}
                    />
                    {socialLink.title}
                  </span>
                </Button>
              </li>
            )
          })}
        </ul>
      </div>
      {showSocials && (
        <Socials
          instagram={instagram}
          twitter={twitter}
          tiktok={tiktok}
          youtube={youtube}
        />
      )}
    </div>
  )
}
